import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import { getSubTraitsForMainAPI } from "../../redux/apis/products";

const RuleOptionIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="15"
    viewBox="0 0 14 15"
    fill="none"
    style={{ marginRight: 4, flexShrink: 0 }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M12.2978 7.50022C12.2714 10.3996 9.89959 12.7286 7.00022 12.7022C4.10084 12.6758 1.77185 10.3039 1.79826 7.40457L1.84608 2.15479L12.3456 2.25044L12.2978 7.50022ZM7.00891 11.7477C9.38113 11.7693 11.3217 9.86374 11.3433 7.49152L11.3824 3.19625L2.79189 3.11799L2.75277 7.41327C2.73116 9.78548 4.6367 11.7261 7.00891 11.7477Z"
      fill="#475CC7"
    />
  </svg>
);

/**
 * SubTraitList fetches sub-traits for a selected main trait (traitId) and displays them.
 * On select, calls changeHandler with the selected sub-trait object.
 */
export default function SubTraitList({
  traitId = null,
  changeHandler = () => {},
  inputValue = null,
  setAnchorEl,
  platformType = "hemp",
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [subTraitsList, setSubTraitsList] = useState([]);

  const fetchSubTraits = useCallback(() => {
    if (!traitId) {
      setSubTraitsList([]);
      return;
    }
    setIsLoading(true);
    getSubTraitsForMainAPI({
      traitId,
      platformType: platformType || "hemp",
      skip: 0,
      limit: 100,
      userType: "admin",
    })
      .then((res) => {
        const list = res?.subTraits ?? res?.data ?? (Array.isArray(res) ? res : []);
        setSubTraitsList(Array.isArray(list) ? list : []);
      })
      .catch(() => setSubTraitsList([]))
      .finally(() => setIsLoading(false));
  }, [traitId, platformType]);

  useEffect(() => {
    fetchSubTraits();
  }, [fetchSubTraits]);

  const selectedList = Array.isArray(inputValue) ? inputValue : (inputValue ? [inputValue] : []);

  const isSelected = (subTrait) => {
    const id = subTrait?._id ?? subTrait?.id;
    return selectedList.some((s) => (s?._id ?? s?.id) === id);
  };

  const handleToggle = (subTrait) => {
    const id = subTrait?._id ?? subTrait?.id;
    const next = isSelected(subTrait)
      ? selectedList.filter((s) => (s?._id ?? s?.id) !== id)
      : [...selectedList, subTrait];
    changeHandler({ ruleValue: next });
  };

  if (!traitId) {
    return (
      <Box sx={{ p: 2, minWidth: 220 }}>
        <Typography variant="body2" color="text.secondary">Select a trait first</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 220 }}>
      {!isLoading && subTraitsList.length === 0 && (
        <Box sx={{ p: 2, transform: "scale(0.9)" }}>No sub-traits found</Box>
      )}
      <Box sx={{ maxHeight: 300, overflow: "auto" }}>
        {subTraitsList.map((subTrait, index) => {
          const selected = isSelected(subTrait);
          const name = subTrait?.subTraitName ?? subTrait?.name ?? subTrait?.slug ?? "-";
          return (
            <MenuItem
              key={subTrait?._id ?? subTrait?.id ?? index}
              selected={selected}
              onClick={() => {
                handleToggle(subTrait);
              }}
              sx={{ py: 1 }}
            >
              <RuleOptionIcon />
              <Typography sx={{ paddingLeft: 1 }} color={selected ? "primary" : "textPrimary"} fontWeight={selected ? 600 : 400}>
                {name}
              </Typography>
            </MenuItem>
          );
        })}
      </Box>
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
          <CircularProgress size={24} color="primary" />
        </Box>
      )}
    </Box>
  );
}
